import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/design-system/Button';
import { Input } from '@/components/design-system/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/design-system/popover';
import { CloseOutlined, SearchOutlined, TableOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { ColumnPickerList } from './ColumnPickerList';

export interface ColumnDefinition {
  id: string;
  label: string;
  defaultVisible?: boolean;
  canHide?: boolean;
}

interface ColumnPickerProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onVisibilityChange: (visibleColumnIds: string[]) => void;
  'data-testid'?: string;
}

export const ColumnPicker = ({
  columns,
  visibleColumns,
  onVisibilityChange,
  'data-testid': dataTestId = 'column-picker'
}: ColumnPickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const hideableColumns = useMemo(() => columns.filter((col) => col.canHide !== false), [columns]);

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return hideableColumns;
    const query = searchQuery.toLowerCase();
    return hideableColumns.filter((col) => col.label.toLowerCase().includes(query));
  }, [hideableColumns, searchQuery]);

  const visibleCount = useMemo(
    () => hideableColumns.filter((col) => visibleColumns.includes(col.id)).length,
    [hideableColumns, visibleColumns]
  );

  const handleToggleColumn = useCallback(
    (columnId: string, checked: boolean) => {
      onVisibilityChange(checked ? [...visibleColumns, columnId] : visibleColumns.filter((id) => id !== columnId));
    },
    [visibleColumns, onVisibilityChange]
  );

  const handleShowAll = useCallback(() => {
    const allHideableIds = hideableColumns.map((col) => col.id);
    const nonHideableIds = columns.filter((col) => col.canHide === false).map((col) => col.id);
    onVisibilityChange([...nonHideableIds, ...allHideableIds]);
  }, [columns, hideableColumns, onVisibilityChange]);

  const handleResetToDefault = useCallback(() => {
    onVisibilityChange(columns.filter((col) => col.defaultVisible !== false).map((col) => col.id));
  }, [columns, onVisibilityChange]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="default" size="sm" data-testid={dataTestId}>
          <TableOutlined />
          {t('grid.columnPicker.columns')}
          <span className="font-normal opacity-60">
            ({visibleCount}/{hideableColumns.length})
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0">
        <div className="border-border flex items-center justify-between border-b p-3">
          <span className="text-typography-900 text-sm font-medium">{t('grid.columnPicker.toggleColumns')}</span>
          <Button
            variant="ghost"
            size="xs"
            className="h-auto w-auto min-w-0 p-0.5"
            onClick={handleClose}
            data-testid={`${dataTestId}-close`}
          >
            <CloseOutlined />
          </Button>
        </div>

        <div className="border-border relative border-b p-3">
          <SearchOutlined className="text-typography-900 pointer-events-none absolute top-1/2 left-[22px] -translate-y-1/2 text-sm opacity-50" />
          <Input
            size="sm"
            placeholder={t('grid.columnPicker.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="h-9! pr-8! pl-8! text-sm!"
            data-testid={`${dataTestId}-search`}
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="xs"
              className="absolute top-1/2 right-4 h-auto w-auto min-w-0 -translate-y-1/2 p-0.5"
              onClick={handleClearSearch}
              data-testid={`${dataTestId}-clear-search`}
            >
              <CloseOutlined />
            </Button>
          )}
        </div>

        <ColumnPickerList
          columns={filteredColumns}
          visibleColumns={visibleColumns}
          onToggle={handleToggleColumn}
          dataTestId={dataTestId}
        />

        <div className="border-border flex items-center gap-2 border-t p-3">
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={handleShowAll}
            data-testid={`${dataTestId}-show-all`}
          >
            {t('grid.columnPicker.showAll')}
          </Button>
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={handleResetToDefault}
            data-testid={`${dataTestId}-reset`}
          >
            {t('grid.columnPicker.resetToDefault')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
